
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    category: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', formData.email);
      formBody.append('category', formData.category);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4uukvucd4084g36c050', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ email: '', category: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-6">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <ol className="flex items-center gap-2 text-sm text-text-secondary">
              <li>
                <Link to="/" className="hover:text-text-primary transition-colors cursor-pointer">
                  ホーム
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line"></i>
              </li>
              <li className="text-text-primary font-medium">お問い合わせ</li>
            </ol>
          </nav>

          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-black font-maru text-text-primary mb-4">
              お問い合わせ
            </h1>
            <p className="text-text-secondary">
              ご質問やご要望をお聞かせください
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-3xl shadow-xl p-10">
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="space-y-6">
                {/* Email Field */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-text-primary font-bold text-sm mb-2"
                  >
                    メールアドレス <span className="text-cta">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-text-secondary/30 rounded-lg focus:outline-none focus:border-primary transition-colors"
                    placeholder="example@email.com"
                  />
                </div>

                {/* Category Field */}
                <div>
                  <label
                    htmlFor="category"
                    className="block text-text-primary font-bold text-sm mb-2"
                  >
                    お問い合わせ種別 <span className="text-cta">*</span>
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-text-secondary/30 rounded-lg focus:outline-none focus:border-primary transition-colors cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="機能について">機能について</option>
                    <option value="不具合報告">不具合報告</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                {/* Message Field */}
                <div>
                  <label
                    htmlFor="message"
                    className="block text-text-primary font-bold text-sm mb-2"
                  >
                    お問い合わせ内容 <span className="text-cta">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-text-secondary/30 rounded-lg focus:outline-none focus:border-primary transition-colors resize-none"
                    placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                  ></textarea>
                  <p className="text-text-secondary text-xs mt-2 text-right">
                    {formData.message.length} / 500文字
                  </p>
                </div>

                {/* Submit Button */}
                <div className="text-center pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-12 py-4 bg-cta text-white font-bold rounded-lg hover:bg-cta/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? '送信中...' : '送信する'}
                  </button>
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-accent-green/20 border-2 border-accent-green rounded-lg p-4 text-center">
                    <p className="text-text-primary font-medium">
                      お問い合わせを受け付けました。ありがとうございます！
                    </p>
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="bg-cta/20 border-2 border-cta rounded-lg p-4 text-center">
                    <p className="text-text-primary font-medium">
                      送信に失敗しました。もう一度お試しください。
                    </p>
                  </div>
                )}
              </div>
            </form>

            {/* Additional Info */}
            <div className="mt-8 pt-8 border-t border-text-secondary/20 text-center">
              <p className="text-text-secondary text-sm">
                通常1-2営業日以内にご返信いたします
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
